import { useState, useEffect } from "react";
import "../Leads.css";
import { Undo2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { mapLeadData } from "../../../utils/common";
import { ADDED_LEADS as columns } from "../../../utils/column_maps";
import LoadingSpinner from "../../../components/LoadingSpinner";
import axiosInstance from "../../../api/axiosInstance";
import UndoLeadModal from "../../../components/UndoModal";
import AddLeadForm from "./AddLeadForm";

const AddedLeads = () => {
  const [fetchLoading, setFetchLoading] = useState(true);
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [searchAddress, setSearchAddress] = useState("");

  const [selectedRow, setSelectedRow] = useState(null);
  const [undoModal, setUndoModal] = useState(false);

  useEffect(() => {
    const leadParam = searchParams.get("lead");

    if (leadParam) {
      try {
        const parsedLead = JSON.parse(leadParam);

        let sourceValue = "";
        const t = parsedLead?.refLeadType?.toLowerCase();

        if (t === "lead-s") sourceValue = "S";
        else if (t === "lead-m") sourceValue = "M";
        else if (t === "lead-b") sourceValue = "B";
        else if (t === "lead-d")
          sourceValue = "D"; // eslint-disable-next-line
        else sourceValue = "";
      } catch (err) {
        console.error("Invalid lead data", err);
      }
    }
  }, [searchParams]);

  const [leads, setLeads] = useState([]);

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;

  const totalPages = Math.ceil((totalLeads || 0) / leadsPerPage);

  const fetchLeads = async (page = 1, limit = leadsPerPage, showLoader = true) => {
    try {
      if (showLoader) setFetchLoading(true);
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("page", page);
      setSearchParams(newParams);
      const offset = (page - 1) * limit;
      let leadsUrl = `${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/leads?limit=${limit}&offset=${offset}&owner=true&fpPhones=true&status=contact_saved&values=true&sortBy=created_at&sortOrder=DESC&excludeAllWrongPhones=true&ai_data=true`;
      if (searchAddress) {
        leadsUrl += `&address=${encodeURIComponent(searchAddress)}`;
      }
      const resp = await axiosInstance.get(leadsUrl);
      const data = await resp.data;
      if (data.success) {
        const sortedLeads = (data.leads || []).sort((a, b) => (b.id || 0) - (a.id || 0));
        // setLeads(sortedLeads);
        // setTotalLeads(data.total);
        setCurrentPage(page);

        setLeads((prevLeads) => {
          const prevMap = Object.fromEntries(prevLeads.map((l) => [l.id, l]));
          return sortedLeads.map((newLead) => {
            const prev = prevMap[newLead.id];
            return {
              ...newLead,
              // Preserve existing marketData and loading flag if they exist
              marketData: prev?.marketData ?? newLead.marketData,
              marketDataLoading: prev?.marketDataLoading ?? false,
            };
          });
        });

        setTotalLeads(data.total);

        const addressList = sortedLeads.map((l) => l.full_address).filter(Boolean);
        const zipList = [...new Set(sortedLeads.map((l) => l.zip).filter(Boolean))];

        if (showLoader && addressList.length && zipList.length) {
          fetchMarketDataBatch(addressList, zipList, showLoader);
        }
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchMarketDataBatch = async (addressList = [], zipList = [], showLoader = true) => {
    if (!addressList?.length || !zipList?.length) return;

    // Only show loading indicators if explicitly requested
    if (showLoader) {
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: true } : lead)));
    }

    try {
      const response = await axiosInstance.post(`/api/webhooks/market-data-batch`, {
        full_address: addressList,
      });

      if (response.data?.success) {
        let marketDataMap = {};
        if (Array.isArray(response.data.data)) {
          marketDataMap = Object.fromEntries(response.data.data.map((item) => [item.zip, item]));
        }

        setLeads((prevLeads) =>
          prevLeads.map((lead) => {
            const marketData = marketDataMap[lead.zip];
            // Always update marketData and clear loading for that lead
            return {
              ...lead,
              marketData: marketData ? marketData.normalizedMarket : lead.marketData,
              marketDataLoading: false,
            };
          }),
        );
      } else {
        // On API failure, clear loading for all affected leads
        setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
      }
    } catch (error) {
      console.error("Failed to fetch market data", error);
      setLeads((prevLeads) => prevLeads.map((lead) => (zipList.includes(lead.zip) ? { ...lead, marketDataLoading: false } : lead)));
    }
  };

  useEffect(() => {
    fetchLeads(currentPage, leadsPerPage, true);
    const interval = setInterval(() => {
      fetchLeads(currentPage, leadsPerPage, false);
    }, 10000);
    return () => clearInterval(interval); // eslint-disable-next-line
  }, [currentPage, searchAddress]);

  const handleAddressSearch = (address) => {
    setSearchAddress(address);
    setCurrentPage(1);
  };

  const handleClearAddressSearch = () => {
    setSearchAddress("");
    setCurrentPage(1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  const handleUndo = async (id, complete = false) => {
    try {
      const res = await axiosInstance.delete(`api/dealmachine_properties/lead/${id}?complete=${complete}`);

      if (res.data.success) {
        console.log("Lead undo successful:", res.data);
        fetchLeads();
      } else {
        console.warn("Lead undo failed:", res.data.message);
      }
    } catch (error) {
      console.error("Error undo lead:", error.message || error);
    }
  };

  return (
    <>
      <div>
        <AddLeadForm onSearch={handleAddressSearch} onClear={handleClearAddressSearch} />
        <div className="mt-6 !w-full !overflow-x-auto">
          <table className="!border !border-gray-300 !table-auto min-w-[98%]" style={{ width: `${totalTableWidth}px` }}>
            <thead>
              <tr className="!bg-gray-200">
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-4 !py-2`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                      textAlign: col.headerAlign,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!fetchLoading ? (
                <>
                  {leads.length > 0 ? (
                    leads.map((lead, idx) => {
                      const row = mapLeadData(lead);
                      return (
                        <tr key={lead.id || idx} className="!hover:bg-gray-100">
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <button
                              onClick={() => {
                                setUndoModal(true);
                                setSelectedRow(lead);
                              }}
                              className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer"
                              title="Undo Lead"
                            >
                              <Undo2 size={18} />
                            </button>
                          </td>
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className={`truncate !border !border-gray-300 !px-4 !py-2 text-sm
                            ${row[col.key] === "Not Found" ? "text-red-500" : /(CONFIRMED)/i.test((row[col.key] ?? "").toString()) ? "text-green-500" : /(NO-ANSWER)/i.test((row[col.key] ?? "").toString()) ? "text-yellow-500" : "text-black"}`}
                              style={{
                                minWidth: `${col.width}px`,
                                width: `${col.width}px`,
                                textAlign: col.cellAlign,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td colSpan={columns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col items-center !mb-10">
          <span className="text-sm text-gray-700">
            Showing <span className="font-semibold text-gray-900">{indexOfFirstLead + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastLead, totalLeads || 0)}</span> of <span className="font-semibold text-gray-900">{totalLeads || 0}</span> Entries
          </span>

          {/* Pagination */}
          <div className="inline-flex !mt-2 xs:mt-0">
            {/* Prev Button */}
            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => fetchLeads(currentPage - 1)} disabled={currentPage === 1}>
              Prev
            </button>

            {/* First Page */}
            <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(1)} disabled={currentPage === 1}>
              1
            </button>

            {/* Ellipses before middle pages */}
            {currentPage > 3 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

            {/* Middle Pages */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => page !== 1 && page !== totalPages && page >= currentPage - 1 && page <= currentPage + 1)
              .map((page) => (
                <button key={page} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${currentPage === page ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(page)}>
                  {page}
                </button>
              ))}

            {/* Ellipses after middle pages */}
            {currentPage < totalPages - 2 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

            {/* Last Page */}
            {totalPages > 1 && (
              <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(totalPages)} disabled={currentPage === totalPages}>
                {totalPages}
              </button>
            )}

            {/* Next Button */}
            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => fetchLeads(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>

        <UndoLeadModal
          isOpen={undoModal}
          title="Undo Lead"
          message="Do you want to undo this lead completely or only in Deal Machine?"
          onCancel={() => {
            setUndoModal(false);
            setSelectedRow(null);
          }}
          onCompleteUndo={() => handleUndo(selectedRow.id, true)}
          onDealMachineUndo={() => handleUndo(selectedRow.id, false)}
          buttonColor="red"
        />
      </div>
    </>
  );
};

export default AddedLeads;
